/**
 * ProceduralLevel — Generates a side-scrolling level procedurally.
 *
 * Creates platforms and ground using Phaser.GameObjects.Rectangle with static physics bodies.
 * Supports different layouts per map (Floresta Encantada vs Montanha Gelada).
 *
 * Each map has 5 zones across 25000px:
 * - Floresta: gentler, tutorial-friendly, small gaps
 * - Montanha Gelada: harder, more gaps, narrower platforms, more elevation changes
 *
 * Requirements: 1.1, 1.2
 */

import Phaser from 'phaser';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Total world width in pixels */
const WORLD_WIDTH = 25000;

/** World height in pixels */
const WORLD_HEIGHT = 720;

/** Tile size used for grid alignment */
const TILE_SIZE = 64;

/** Ground Y position (top of ground) */
const GROUND_Y = WORLD_HEIGHT - TILE_SIZE;

/** Colors for Floresta Encantada */
const FOREST_COLORS = {
  ground: 0x228b22,       // Forest green
  platform: 0x8b4513,     // Saddle brown
  platformTop: 0x654321,  // Darker brown
  marker: 0x654321,       // Same as platformTop (no highlight border)
} as const;

/** Colors for Montanha Gelada */
const ICE_COLORS = {
  ground: 0x5588aa,       // Icy blue-grey
  platform: 0x7799bb,     // Light ice blue
  platformTop: 0x4477aa,  // Deeper ice blue
  marker: 0x4477aa,       // Same as platformTop
} as const;

/** Colors for Reino Encantado */
const CASTLE_COLORS = {
  ground: 0x666680,       // Grey stone
  platform: 0x555570,     // Dark stone platform
  platformTop: 0x444460,  // Darker stone
  marker: 0x444460,       // Same as platformTop
} as const;

/** Colors for Espaço Sideral */
const SPACE_COLORS = {
  ground: 0x8899aa,       // Silver metal
  platform: 0x4466aa,     // Blue-edge platform
  platformTop: 0x334488,  // Deep blue
  marker: 0x334488,       // Same as platformTop
} as const;

/** Zone boundaries (start x) */
const ZONES = {
  zone1: { start: 0, end: 5000 },
  zone2: { start: 5000, end: 10000 },
  zone3: { start: 10000, end: 15000 },
  zone4: { start: 15000, end: 20000 },
  zone5: { start: 20000, end: 25000 },
} as const;

// ─── Result Interface ────────────────────────────────────────────────────────

export interface ProceduralLevelResult {
  /** Static group containing all ground and platform bodies */
  platforms: Phaser.Physics.Arcade.StaticGroup;
  /** Total world width for camera/physics bounds */
  worldWidth: number;
}

// ─── Level Generator ─────────────────────────────────────────────────────────

/**
 * Generates a procedural side-scrolling level with 5 difficulty zones.
 * Layout differs based on mapId.
 *
 * @param scene - The Phaser scene to create game objects in
 * @param mapId - Optional map identifier (default = floresta)
 * @returns Object with the static platforms group and world width
 */
export function generateLevel(scene: Phaser.Scene, mapId?: string): ProceduralLevelResult {
  const platforms = scene.physics.add.staticGroup();
  const isReino = mapId === 'reino-encantado';
  const isEspaco = mapId === 'espaco-sideral';
  const isMontanha = mapId === 'montanha-gelada';

  if (isEspaco) {
    const colors = SPACE_COLORS;
    const groundTex = 'espaco-ground';
    const platformTex = 'espaco-platform';
    generateEspacoZone1(scene, platforms, colors, groundTex, platformTex);
    generateEspacoZone2(scene, platforms, colors, groundTex, platformTex);
    generateEspacoZone3(scene, platforms, colors, groundTex, platformTex);
    generateEspacoZone4(scene, platforms, colors, groundTex, platformTex);
    generateEspacoZone5(scene, platforms, colors, groundTex, platformTex);
  } else if (isReino) {
    const colors = CASTLE_COLORS;
    const groundTex = 'reino-ground';
    const platformTex = 'reino-platform';
    generateReinoZone1(scene, platforms, colors, groundTex, platformTex);
    generateReinoZone2(scene, platforms, colors, groundTex, platformTex);
    generateReinoZone3(scene, platforms, colors, groundTex, platformTex);
    generateReinoZone4(scene, platforms, colors, groundTex, platformTex);
    generateReinoZone5(scene, platforms, colors, groundTex, platformTex);
  } else if (isMontanha) {
    const colors = ICE_COLORS;
    const groundTex = 'montanha-ground';
    const platformTex = 'montanha-platform';
    generateMontanhaZone1(scene, platforms, colors, groundTex, platformTex);
    generateMontanhaZone2(scene, platforms, colors, groundTex, platformTex);
    generateMontanhaZone3(scene, platforms, colors, groundTex, platformTex);
    generateMontanhaZone4(scene, platforms, colors, groundTex, platformTex);
    generateMontanhaZone5(scene, platforms, colors, groundTex, platformTex);
  } else {
    const colors = FOREST_COLORS;
    const groundTex = 'floresta-ground';
    const platformTex = 'floresta-platform';
    generateFlorestaZone1(scene, platforms, colors, groundTex, platformTex);
    generateFlorestaZone2(scene, platforms, colors, groundTex, platformTex);
    generateFlorestaZone3(scene, platforms, colors, groundTex, platformTex);
    generateFlorestaZone4(scene, platforms, colors, groundTex, platformTex);
    generateFlorestaZone5(scene, platforms, colors, groundTex, platformTex);
  }

  return { platforms, worldWidth: WORLD_WIDTH };
}

// ═══════════════════════════════════════════════════════════════════════════════
// FLORESTA ENCANTADA — Easier, wider platforms, smaller gaps
// ═══════════════════════════════════════════════════════════════════════════════

function generateFlorestaZone1(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof FOREST_COLORS, gTex: string, pTex: string): void {
  const { start, end } = ZONES.zone1;
  const width = end - start;
  const T = TILE_SIZE;

  addPlatform(scene, platforms, start + width / 2, GROUND_Y + T / 2, width, T, C.ground, gTex);

  addPlatform(scene, platforms, 800, GROUND_Y - T * 2.5, T * 5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, 1800, GROUND_Y - T * 4, T * 6, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, 2800, GROUND_Y - T * 3, T * 5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, 3800, GROUND_Y - T * 5, T * 4, T * 0.5, C.platform, pTex);
}

function generateFlorestaZone2(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof FOREST_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone2.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  const groundPlatforms: [number, number][] = [
    [s + 400, 10], [s + 1170, 8], [s + 1600, 10], [s + 2300, 12],
    [s + 3100, 10], [s + 3800, 12], [s + 4600, 8],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }
  addPlatform(scene, platforms, s + 1000, GROUND_Y - T * 3, T * 3, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 2700, GROUND_Y - T * 2.5, T * 3, T * 0.5, C.platform, pTex);
}

function generateFlorestaZone3(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof FOREST_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone3.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  const groundPlatforms: [number, number][] = [
    [s + 400, 10], [s + 1200, 10], [s + 2000, 10],
    [s + 2800, 12], [s + 3700, 10], [s + 4500, 8],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }
  addPlatform(scene, platforms, s + 800, GROUND_Y - T * 3, T * 2.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1600, GROUND_Y - T * 4, T * 2, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 3300, GROUND_Y - T * 3, T * 2.5, T * 0.5, C.platform, pTex);
}

function generateFlorestaZone4(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof FOREST_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone4.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  const groundPlatforms: [number, number][] = [
    [s + 400, 10], [s + 1200, 8], [s + 1900, 10],
    [s + 2700, 12], [s + 3600, 10], [s + 4400, 8],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }
  addPlatform(scene, platforms, s + 800, GROUND_Y - T * 2, T * 2, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 1600, GROUND_Y - T * 3, T * 2, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 3200, GROUND_Y - T * 2.5, T * 2, T * 0.5, C.marker, pTex);
}

function generateFlorestaZone5(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof FOREST_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone5.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  const groundPlatforms: [number, number][] = [
    [s + 400, 8], [s + 1100, 8], [s + 1800, 10],
    [s + 2600, 12], [s + 3500, 10], [s + 4300, 8],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }
  addPlatform(scene, platforms, s + 700, GROUND_Y - T * 2, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1500, GROUND_Y - T * 3, T * 2, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 3100, GROUND_Y - T * 3, T * 2, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 3900, GROUND_Y - T * 4, T * 2, T * 0.5, C.platformTop, pTex);
}

// ═══════════════════════════════════════════════════════════════════════════════
// MONTANHA GELADA — Harder, narrower platforms, more gaps, more elevation
// ═══════════════════════════════════════════════════════════════════════════════

function generateMontanhaZone1(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof ICE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone1.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  const groundPlatforms: [number, number][] = [
    [s + 500, 12], [s + 1400, 10], [s + 2200, 8],
    [s + 2900, 10], [s + 3700, 12], [s + 4600, 6],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }
  addPlatform(scene, platforms, s + 4246, GROUND_Y - T * 1.5, T * 2.5, T * 0.5, C.platform, pTex); // stepping stone (gap=324px)

  addPlatform(scene, platforms, 1000, GROUND_Y - T * 3, T * 3, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, 2000, GROUND_Y - T * 4.5, T * 3, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, 3200, GROUND_Y - T * 3.5, T * 3, T * 0.5, C.platform, pTex);
}

function generateMontanhaZone2(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof ICE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone2.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition platform from Zone 1
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 350, 8], [s + 1050, 7], [s + 1700, 8], [s + 2400, 7],
    [s + 3050, 9], [s + 3750, 10], [s + 4500, 7],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  addPlatform(scene, platforms, s + 700, GROUND_Y - T * 2.5, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1400, GROUND_Y - T * 3.5, T * 2, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 2100, GROUND_Y - T * 2, T * 2.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 3400, GROUND_Y - T * 4, T * 2, T * 0.5, C.marker, pTex);
}

function generateMontanhaZone3(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof ICE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone3.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition platform from Zone 2
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 350, 7], [s + 1000, 7], [s + 1650, 8], [s + 2350, 7],
    [s + 2950, 10], [s + 3700, 7], [s + 4350, 7],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  addPlatform(scene, platforms, s + 600, GROUND_Y - T * 2, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1300, GROUND_Y - T * 3.5, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 2000, GROUND_Y - T * 5, T * 2, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 3300, GROUND_Y - T * 3, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 4000, GROUND_Y - T * 4, T * 1.5, T * 0.5, C.platformTop, pTex);
}

function generateMontanhaZone4(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof ICE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone4.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition platform from Zone 3
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 350, 7], [s + 1000, 6], [s + 1550, 7], [s + 2200, 8],
    [s + 2850, 10], [s + 3600, 7], [s + 4250, 6],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  addPlatform(scene, platforms, s + 650, GROUND_Y - T * 2.5, T * 2, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 1250, GROUND_Y - T * 4, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 1850, GROUND_Y - T * 3, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 3200, GROUND_Y - T * 3.5, T * 2, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 3900, GROUND_Y - T * 5, T * 1.5, T * 0.5, C.marker, pTex);
}

function generateMontanhaZone5(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof ICE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone5.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition platform from Zone 4 (fills gap between zones)
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 350, 6], [s + 950, 6], [s + 1500, 7], [s + 2100, 6],
    [s + 2700, 10], [s + 3450, 7], [s + 4100, 6],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  addPlatform(scene, platforms, s + 600, GROUND_Y - T * 2, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1200, GROUND_Y - T * 3.5, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 1800, GROUND_Y - T * 5, T * 2, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 2400, GROUND_Y - T * 3, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 3100, GROUND_Y - T * 4, T * 2, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 3750, GROUND_Y - T * 5.5, T * 1.5, T * 0.5, C.marker, pTex);

  // Stepping stone between platform 6 and 7 (gap too large to jump directly)
  addPlatform(scene, platforms, s + 3790, GROUND_Y - T * 1.5, T * 2, T * 0.5, C.platform, pTex);
}

// ─── Helper ──────────────────────────────────────────────────────────────────

/**
 * Creates a platform with working physics.
 * Uses colored rectangle for collision. Overlays a visual texture.
 */
function addPlatform(
  scene: Phaser.Scene,
  group: Phaser.Physics.Arcade.StaticGroup,
  x: number,
  y: number,
  width: number,
  height: number,
  color: number,
  textureKey?: string
): Phaser.GameObjects.GameObject {
  // Invisible rectangle for physics/collision (alpha 0 hides it behind the TileSprite)
  const rect = scene.add.rectangle(x, y, width, height, color);
  rect.setAlpha(0);
  group.add(rect);
  const body = rect.body as Phaser.Physics.Arcade.StaticBody;
  if (body) {
    body.updateFromGameObject();
  }

  // Visual texture: use TileSprite (repeats/tiles the texture) for all platforms
  if (textureKey && scene.textures.exists(textureKey)) {
    const tile = scene.add.tileSprite(x, y, width, height, textureKey);
    tile.setDepth(11);
  } else {
    // No texture available — make the rectangle visible as fallback
    rect.setAlpha(1);
  }

  return rect;
}

// ═══════════════════════════════════════════════════════════════════════════════
// REINO ENCANTADO — Medium-hard, slightly narrower than Montanha, multi-tier
// ═══════════════════════════════════════════════════════════════════════════════

function generateReinoZone1(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof CASTLE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone1.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  const groundPlatforms: [number, number][] = [
    [s + 500, 10], [s + 1300, 9], [s + 2100, 8],
    [s + 2800, 9], [s + 3600, 10], [s + 4500, 7],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }
  addPlatform(scene, platforms, s + 4098, GROUND_Y - T * 1.5, T * 2.5, T * 0.5, C.platform, pTex); // stepping stone (gap=356px)

  addPlatform(scene, platforms, s + 900, GROUND_Y - T * 3, T * 3, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1800, GROUND_Y - T * 4, T * 2.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 2500, GROUND_Y - T * 2.5, T * 3, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 3300, GROUND_Y - T * 4.5, T * 2.5, T * 0.5, C.marker, pTex);
}

function generateReinoZone2(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof CASTLE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone2.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition from Zone 1
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 350, 8], [s + 1000, 7], [s + 1600, 7], [s + 2250, 8],
    [s + 2900, 9], [s + 3600, 8], [s + 4300, 7],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  // Multi-tier section
  addPlatform(scene, platforms, s + 700, GROUND_Y - T * 2.5, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 700, GROUND_Y - T * 5, T * 2, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 1350, GROUND_Y - T * 3, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 2600, GROUND_Y - T * 3.5, T * 2, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 3300, GROUND_Y - T * 4, T * 2, T * 0.5, C.platformTop, pTex);
}

function generateReinoZone3(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof CASTLE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone3.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition from Zone 2
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 350, 7], [s + 1000, 7], [s + 1600, 7], [s + 2250, 7],
    [s + 2850, 9], [s + 3550, 7], [s + 4200, 6],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  // Multi-tier castle section
  addPlatform(scene, platforms, s + 600, GROUND_Y - T * 2, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 600, GROUND_Y - T * 4.5, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 1300, GROUND_Y - T * 3.5, T * 2, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 2000, GROUND_Y - T * 5, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 3200, GROUND_Y - T * 3, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 3900, GROUND_Y - T * 4.5, T * 1.5, T * 0.5, C.marker, pTex);
}

function generateReinoZone4(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof CASTLE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone4.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition from Zone 3
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 350, 7], [s + 950, 6], [s + 1500, 7], [s + 2100, 7],
    [s + 2750, 9], [s + 3500, 6], [s + 4100, 6],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  addPlatform(scene, platforms, s + 600, GROUND_Y - T * 2.5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 1200, GROUND_Y - T * 4, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 1200, GROUND_Y - T * 6, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 1800, GROUND_Y - T * 3, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 3200, GROUND_Y - T * 3.5, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 3800, GROUND_Y - T * 5, T * 1.5, T * 0.5, C.marker, pTex);
}

function generateReinoZone5(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof CASTLE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone5.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition from Zone 4
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 350, 6], [s + 900, 6], [s + 1450, 7], [s + 2050, 6],
    [s + 2650, 9], [s + 3350, 6], [s + 3950, 6],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  addPlatform(scene, platforms, s + 550, GROUND_Y - T * 2, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1100, GROUND_Y - T * 3.5, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 1100, GROUND_Y - T * 5.5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 1700, GROUND_Y - T * 4, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 2400, GROUND_Y - T * 3, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 3050, GROUND_Y - T * 4.5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 3700, GROUND_Y - T * 5.5, T * 1.5, T * 0.5, C.platformTop, pTex);
}

// ═══════════════════════════════════════════════════════════════════════════════
// ESPAÇO SIDERAL — Hardest: narrowest platforms, most gaps, lots of elevation
// ═══════════════════════════════════════════════════════════════════════════════

function generateEspacoZone1(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof SPACE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone1.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  const groundPlatforms: [number, number][] = [
    [s + 450, 8], [s + 1200, 7], [s + 1900, 6],
    [s + 2500, 7], [s + 3200, 8], [s + 3900, 6], [s + 4500, 5],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }
  addPlatform(scene, platforms, s + 1566, GROUND_Y - T * 1.5, T * 2.5, T * 0.5, C.platform, pTex); // stepping stone (gap=284px)

  addPlatform(scene, platforms, s + 800, GROUND_Y - T * 3, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1600, GROUND_Y - T * 4.5, T * 2, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 2200, GROUND_Y - T * 3, T * 2, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 3500, GROUND_Y - T * 4, T * 2, T * 0.5, C.marker, pTex);
}

function generateEspacoZone2(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof SPACE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone2.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition from Zone 1
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 300, 6], [s + 900, 6], [s + 1450, 6], [s + 2000, 5],
    [s + 2550, 7], [s + 3200, 6], [s + 3800, 5], [s + 4400, 5],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  addPlatform(scene, platforms, s + 600, GROUND_Y - T * 2.5, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1200, GROUND_Y - T * 4, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 1750, GROUND_Y - T * 5.5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 2800, GROUND_Y - T * 3, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 3500, GROUND_Y - T * 4.5, T * 1.5, T * 0.5, C.platformTop, pTex);
}

function generateEspacoZone3(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof SPACE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone3.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition from Zone 2
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 300, 5], [s + 850, 5], [s + 1350, 6], [s + 1900, 5],
    [s + 2450, 7], [s + 3100, 5], [s + 3650, 5], [s + 4200, 5],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  addPlatform(scene, platforms, s + 550, GROUND_Y - T * 2, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1100, GROUND_Y - T * 3.5, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 1100, GROUND_Y - T * 5.5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 1650, GROUND_Y - T * 4, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 2750, GROUND_Y - T * 3, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 3350, GROUND_Y - T * 5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 3900, GROUND_Y - T * 3.5, T * 1.5, T * 0.5, C.platform, pTex);
}

function generateEspacoZone4(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof SPACE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone4.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition from Zone 3
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 300, 5], [s + 800, 5], [s + 1300, 5], [s + 1800, 5],
    [s + 2350, 6], [s + 2950, 5], [s + 3500, 5], [s + 4050, 5],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  addPlatform(scene, platforms, s + 550, GROUND_Y - T * 2.5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 1050, GROUND_Y - T * 4, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 1550, GROUND_Y - T * 5.5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 1550, GROUND_Y - T * 3, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 2600, GROUND_Y - T * 3.5, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 3200, GROUND_Y - T * 5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 3750, GROUND_Y - T * 3.5, T * 1.5, T * 0.5, C.platform, pTex);
}

function generateEspacoZone5(scene: Phaser.Scene, platforms: Phaser.Physics.Arcade.StaticGroup, C: typeof SPACE_COLORS, gTex: string, pTex: string): void {
  const s = ZONES.zone5.start;
  const G = GROUND_Y + TILE_SIZE / 2;
  const T = TILE_SIZE;

  // Transition from Zone 4
  addPlatform(scene, platforms, s - 200, G, T * 4, T, C.ground, gTex);

  const groundPlatforms: [number, number][] = [
    [s + 300, 5], [s + 800, 4], [s + 1250, 5], [s + 1700, 4],
    [s + 2200, 6], [s + 2800, 5], [s + 3350, 4], [s + 3850, 4], [s + 4350, 5],
  ];
  for (const [cx, tiles] of groundPlatforms) {
    addPlatform(scene, platforms, cx, G, T * tiles, T, C.ground, gTex);
  }

  addPlatform(scene, platforms, s + 550, GROUND_Y - T * 2, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 1000, GROUND_Y - T * 3.5, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 1000, GROUND_Y - T * 5.5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 1500, GROUND_Y - T * 4, T * 1.5, T * 0.5, C.platform, pTex);
  addPlatform(scene, platforms, s + 2000, GROUND_Y - T * 5.5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 2500, GROUND_Y - T * 3, T * 1.5, T * 0.5, C.platformTop, pTex);
  addPlatform(scene, platforms, s + 3100, GROUND_Y - T * 4.5, T * 1.5, T * 0.5, C.marker, pTex);
  addPlatform(scene, platforms, s + 3600, GROUND_Y - T * 6, T * 1.5, T * 0.5, C.platformTop, pTex);
}
